import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ImageOff,
  Loader2,
  Pencil,
  ShieldCheck,
  ShoppingCart,
  Truck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDirectSaleStore } from '@/stores/directSaleStore';
import { useDirectSaleEditorBridge } from './useDirectSaleEditorBridge';
import { useDirectSaleCheckout } from './useDirectSaleCheckout';
import { DirectSaleMockupPreview } from './DirectSaleMockupPreview';
import { formatMoney } from './format-money';
import { describeQuoteError } from './quote-error';

const mmToCm = (mm: number | null) =>
  mm === null ? null : Math.round(mm / 10);

/**
 * Step 3: the customer reviews what they configured and buys it.
 *
 * Every amount shown comes from the server quote and is rendered verbatim.
 * Recomputing anything here would let the displayed price drift from the one
 * handed to the buying system.
 */
export function DirectSaleCheckoutStep() {
  const { t, i18n } = useTranslation();
  const { product } = useDirectSaleEditorBridge();

  const colorId = useDirectSaleStore((s) => s.colorId);
  const sizeId = useDirectSaleStore((s) => s.sizeId);
  const decoratedPositions = useDirectSaleStore((s) => s.decoratedPositions);
  const goToStep = useDirectSaleStore((s) => s.goToStep);

  const [activeIndex, setActiveIndex] = useState(0);
  const { quote, gallery, handleCheckout, isSubmitting, checkoutError } =
    useDirectSaleCheckout(product);

  const color = product?.colors?.find((c) => c.id === colorId) ?? null;
  const size = product?.variants?.find(
    (v) => v.productSizeId === sizeId
  )?.productSize;

  /** Print method of the first decorated position (per the design mock). */
  const printMethod = useMemo(() => {
    const first = decoratedPositions[0];
    const position = product?.positions?.find(
      (p) => p.id === first?.positionId
    );
    // The list endpoint transforms this to a single DecorationMethod, the
    // detail endpoint returns the raw join rows ({ decorationMethod }[]) —
    // read both shapes rather than depending on which endpoint fed the cache.
    const raw = position?.decorationMethods as
      | { name: string }
      | { decorationMethod?: { name: string } }[]
      | null
      | undefined;
    const method = Array.isArray(raw)
      ? (raw[0]?.decorationMethod ?? null)
      : (raw ?? null);
    if (!method) return null;
    return method.name;
  }, [decoratedPositions, product]);
  // The quote names what the customer chose; the position's default is only
  // the fallback while it loads.
  const shownMethod = quote.data?.decorationMethod?.name ?? printMethod;

  const active = gallery[Math.min(activeIndex, gallery.length - 1)];

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-wrap items-start gap-6 px-4 pb-24 pt-6">
      {/* Gallery */}
      <div className="flex min-w-[min(380px,100%)] flex-[999_1_380px] flex-col gap-3">
        <div className="relative flex aspect-square max-h-[560px] items-center justify-center rounded-section bg-[rgb(var(--color-canvas-bg))]">
          {product && active?.preview && active.slug ? (
            // The design alone is not what the customer buys — show it on
            // the product, through the same layer rules as the editor.
            <DirectSaleMockupPreview
              product={product}
              positionSlug={active.slug}
              designDataUrl={active.preview}
              className="h-full w-full"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-mute">
              <ImageOff className="h-8 w-8" />
              <span className="text-xs">
                {t('directSale.checkout.noMockup')}
              </span>
            </div>
          )}

          {active && active.widthMm !== null && active.heightMm !== null && (
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-control bg-black/75 px-3 py-1 text-xs text-white">
              {t('directSale.checkout.positionBadge', {
                position: active.name,
                width: mmToCm(active.widthMm),
                height: mmToCm(active.heightMm),
              })}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-stretch gap-3">
          {gallery.map((entry, index) => (
            <button
              key={entry.positionId}
              type="button"
              aria-label={entry.name}
              aria-pressed={index === activeIndex}
              onClick={() => setActiveIndex(index)}
              className={cn(
                'flex h-[92px] w-20 items-center justify-center rounded-card bg-[rgb(var(--color-canvas-bg))] transition-all',
                index === activeIndex
                  ? 'border-2 border-primary'
                  : 'border border-line hover:border-primary'
              )}
            >
              {product && entry.preview && entry.slug ? (
                <DirectSaleMockupPreview
                  product={product}
                  positionSlug={entry.slug}
                  designDataUrl={entry.preview}
                  className="h-full w-full"
                />
              ) : (
                <ImageOff className="h-5 w-5 text-ink-3" />
              )}
            </button>
          ))}

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => goToStep('design')}
            className="flex min-h-[44px] items-center gap-2 rounded-card border border-line bg-paper-2 px-4 text-[13px] font-medium text-ink-2 transition-colors hover:border-primary hover:text-primary"
          >
            <Pencil className="h-4 w-4" />
            {t('directSale.nav.editDesign')}
          </button>
        </div>

        {/* Trust row from the design mock. The copy is the embedding shop's
            promise, not DesignDeck's — a per-shop source for these values is
            an open follow-up; until then the mock's defaults ship as i18n. */}
        <div className="flex flex-wrap gap-3">
          <div className="flex flex-[1_1_200px] items-center gap-2.5 rounded-card border border-line bg-paper-2 p-3">
            <Truck className="h-5 w-5 flex-none text-success" />
            <div>
              <div className="text-xs font-semibold text-ink">
                {t('directSale.checkout.trustShippingTitle')}
              </div>
              <div className="text-[11px] text-mute">
                {t('directSale.checkout.trustShippingSub')}
              </div>
            </div>
          </div>
          <div className="flex flex-[1_1_200px] items-center gap-2.5 rounded-card border border-line bg-paper-2 p-3">
            <ShieldCheck className="h-5 w-5 flex-none text-primary" />
            <div>
              <div className="text-xs font-semibold text-ink">
                {t('directSale.checkout.trustQualityTitle')}
              </div>
              <div className="text-[11px] text-mute">
                {t('directSale.checkout.trustQualitySub')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="min-w-[min(340px,100%)] flex-[1_1_340px] rounded-section border border-line bg-paper-2 p-6 ">
        <h2 className="text-xl font-extrabold text-ink">
          {product ? product.name : ''}
        </h2>
        <p className="mt-0.5 text-[13px] text-mute">
          {t('directSale.checkout.subtitle', {
            method: shownMethod ? ` · ${shownMethod}` : '',
          })}
        </p>

        <dl className="mt-4 grid grid-cols-2 gap-3">
          <SummaryCell label={t('directSale.checkout.color')}>
            <span className="flex items-center gap-1.5">
              <span
                style={{ backgroundColor: color?.hexCode || '#e5e7eb' }}
                className="h-3.5 w-3.5 rounded-full border border-line"
              />
              {color ? color.name : '—'}
            </span>
          </SummaryCell>

          <SummaryCell label={t('directSale.checkout.size')}>
            <span className="flex items-baseline gap-2">
              {size?.name ?? '—'}
              <button
                type="button"
                onClick={() => goToStep('design')}
                className="text-[11px] font-medium text-primary hover:underline"
              >
                {t('directSale.checkout.change')}
              </button>
            </span>
          </SummaryCell>

          {product?.material && (
            <SummaryCell label={t('directSale.checkout.fabric')}>
              {product.material}
            </SummaryCell>
          )}

          {shownMethod && (
            <SummaryCell label={t('directSale.checkout.printMethod')}>
              {shownMethod}
            </SummaryCell>
          )}
        </dl>

        <div className="mt-4 border-t border-line pt-3.5">
          {quote.isPending && (
            <div className="h-24 animate-pulse rounded-control bg-paper-2" />
          )}

          {quote.isError && (
            <p className="text-sm text-danger">
              {describeQuoteError(
                quote.error,
                t('directSale.checkout.priceFailed')
              )}
            </p>
          )}

          {quote.isSuccess && (
            <>
              <PriceRow
                label={product ? product.name : ''}
                amount={formatMoney(
                  quote.data.basePrice,
                  quote.data.currency,
                  i18n.language
                )}
              />

              {quote.data.positions.map((line) => (
                <PriceRow
                  key={line.positionId}
                  label={t('directSale.checkout.printArea', {
                    position: line.positionName,
                  })}
                  badge={
                    line.widthMm && line.heightMm
                      ? `${mmToCm(line.widthMm)}×${mmToCm(line.heightMm)} cm`
                      : undefined
                  }
                  amount={formatMoney(
                    line.amount,
                    quote.data.currency,
                    i18n.language
                  )}
                />
              ))}

              <div className="mt-2.5 flex items-baseline justify-between border-t border-line pt-3">
                <span className="text-[15px] font-bold text-ink">
                  {t('directSale.checkout.total')}
                </span>
                <span className="text-2xl font-extrabold text-ink">
                  {formatMoney(
                    quote.data.total,
                    quote.data.currency,
                    i18n.language
                  )}
                </span>
              </div>
              <p className="text-[11.5px] text-mute">
                {t('directSale.checkout.vatNote', { rate: quote.data.vatRate })}
              </p>
            </>
          )}
        </div>

        {checkoutError && (
          <p role="alert" className="mt-3 text-[13px] text-danger">
            {checkoutError}
          </p>
        )}
        <button
          type="button"
          onClick={() => void handleCheckout()}
          disabled={!quote.isSuccess || isSubmitting}
          aria-busy={isSubmitting}
          className="mt-4 flex h-13 w-full items-center justify-center gap-2.5 rounded-card bg-gradient-to-r from-primary-500 to-primary-600 py-3.5 text-base font-bold text-white ring-1 ring-line shadow-primary-500/35 transition-[filter] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {t('directSale.checkout.committing')}
            </>
          ) : (
            <>
              <ShoppingCart className="h-5 w-5" />
              {t('directSale.checkout.addToCart')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function SummaryCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-control border border-line bg-paper/60 px-3 py-2.5">
      <dt className="text-[11px] text-mute">{label}</dt>
      <dd className="mt-0.5 text-[13px] font-semibold text-ink">{children}</dd>
    </div>
  );
}

function PriceRow({
  label,
  badge,
  amount,
}: {
  label: string;
  badge?: string;
  amount: string;
}) {
  return (
    <div className="flex justify-between gap-2 py-1 text-[13.5px] text-ink-2">
      <span className="flex flex-wrap items-center gap-1.5">
        {label}
        {badge && (
          <span className="whitespace-nowrap rounded bg-primary-100 px-1.5 py-px text-[10.5px] font-semibold text-primary">
            {badge}
          </span>
        )}
      </span>
      <span>{amount}</span>
    </div>
  );
}
